import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "react-redux";

import { RootState } from "@/redux";

import {
	useDeleteCriteriaCompanyPersonaMutation,
	useDeletePersonaScoreMutation,
	useDeleteScoreMutation,
	useUpdateCriteriaCompanyPersonaMutation,
	useUpdateScoreMutation,
} from "@/pages/Private/redux/score/score.api";
import { scoreSelector } from "@/pages/Private/redux/score/score.slice";

import { saveSegmentAssignments } from "../pages/ScoreOverView/utils/saveSegmentAssignments";
import { ScoreCriteriaType, ScoreResponse } from "../schema/score.schema";

import * as draftOps from "./configDraft";
import {
	CellAssignment,
	cellKey,
	findAssignment,
	isLinked,
	PersonaView,
	personasOf,
	SegmentView,
	segmentsOf,
} from "./scoreConfigView";
import { firstSaveBlocker, SaveBlocker } from "./saveBlocker";
import { saveErrorText } from "./saveErrorText";

/**
 * The unsaved state behind every settings page (PREC-4055): a working copy of
 * the score plus the matrix cells the user changed. One save path for all of
 * them — the score body first, then the deletions, then the cells — so the
 * Overview, the segment page and the persona page cannot disagree about how
 * an edit reaches the server.
 */

type PendingAssignment = { criteriaId: number } | null;

export interface ConfigDraftApi {
	draft: ScoreResponse | null;
	segments: SegmentView[];
	personas: PersonaView[];
	dirty: boolean;
	saving: boolean;
	error: string | null;
	/** The row the last save was refused for (PREC-4987); the page selects and flashes it. */
	blocker: SaveBlocker | null;
	isLinked: (segment: SegmentView, persona: PersonaView) => boolean;
	assignmentOf: (segment: SegmentView, persona: PersonaView) => CellAssignment | null;
	pick: (segment: SegmentView, persona: PersonaView, criteriaId: number) => void;
	clear: (segment: SegmentView, persona: PersonaView) => void;
	setLinked: (segment: SegmentView, persona: PersonaView, linked: boolean) => void;
	rename: (kind: "segment" | "persona", row: SegmentView | PersonaView, name: string) => void;
	updateSegment: (id: number, patch: draftOps.SegmentPatch) => void;
	addSegment: (
		group: "PROSPECT" | "PARTNER",
		seed?: { category?: string; description?: string }
	) => number;
	removeSegment: (id: number) => void;
	setPartners: (on: boolean) => void;
	setInvestors: (on: boolean) => void;
	setInvestorSelected: (investorName: string, selected: boolean) => void;
	updatePersona: (id: number, patch: draftOps.PersonaPatch) => void;
	addPersona: (seed?: { category?: string; description?: string }) => number;
	removePersona: (id: number) => void;
	/** `remapCriteriaId` translates a temporary scoring id a cell was pointed at into the id the API assigned. */
	save: (remapCriteriaId?: (criteriaId: number) => number) => Promise<boolean>;
	discard: () => void;
}

export const useConfigDraft = (
	score: ScoreResponse | null | undefined,
	criteria: ScoreCriteriaType[],
	refetch: () => Promise<unknown>,
	texts: { saveError: string; blocked: (blocker: SaveBlocker) => string },
	/** Receives the score the API returned after a successful PATCH (the persona page reads its Knowledge DB changes). */
	onSaved?: (saved: ScoreResponse) => void
): ConfigDraftApi => {
	const store = useStore<RootState>();
	const [updateScore] = useUpdateScoreMutation();
	const [deleteSegment] = useDeleteScoreMutation();
	const [deletePersona] = useDeletePersonaScoreMutation();
	const [assignCell] = useUpdateCriteriaCompanyPersonaMutation();
	const [removeCell] = useDeleteCriteriaCompanyPersonaMutation();

	const [draft, setDraft] = useState<ScoreResponse | null>(null);
	const [assignments, setAssignments] = useState<Map<string, PendingAssignment>>(new Map());
	const [pendingSegmentDeletes, setPendingSegmentDeletes] = useState<number[]>([]);
	const [pendingPersonaDeletes, setPendingPersonaDeletes] = useState<number[]>([]);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [blocker, setBlocker] = useState<SaveBlocker | null>(null);

	// Keyed on content: the score query refetches on mount and hands the slice an equal object.
	const scoreKey = score ? JSON.stringify(score) : "";
	const draftDirty = !!draft && !!score && draftOps.isDraftDirty(draft, score);
	const dirty =
		draftDirty ||
		assignments.size > 0 ||
		pendingSegmentDeletes.length > 0 ||
		pendingPersonaDeletes.length > 0;

	useEffect(() => {
		if (score && !dirty) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setDraft(score);
		}
		// Only a changed server copy re-seeds the draft; unsaved edits are kept.
	}, [scoreKey]);

	const segments = useMemo(() => segmentsOf(draft), [draft]);
	const personas = useMemo(() => personasOf(draft), [draft]);

	const edit = useCallback((update: (current: ScoreResponse) => ScoreResponse) => {
		setDraft((current) => (current ? update(current) : current));
	}, []);

	const linked = useCallback(
		(segment: SegmentView, persona: PersonaView) => isLinked(persona, segment),
		[]
	);

	const assignmentOf = useCallback(
		(segment: SegmentView, persona: PersonaView): CellAssignment | null => {
			const key = cellKey(segment, persona.id);

			if (!assignments.has(key)) {
				return findAssignment(criteria, segment, persona.id);
			}

			const pending = assignments.get(key);
			const scoring = pending ? criteria.find((row) => row.id === pending.criteriaId) : null;

			return scoring
				? { criteriaId: scoring.id, criteriaName: scoring.name, locked: scoring.locked, ccpId: -1 }
				: null;
		},
		[assignments, criteria]
	);

	const setLinked = useCallback(
		(segment: SegmentView, persona: PersonaView, on: boolean) =>
			edit((current) => draftOps.setLink(current, segment, persona.id, on)),
		[edit]
	);

	const pick = useCallback(
		(segment: SegmentView, persona: PersonaView, criteriaId: number) => {
			setAssignments((current) =>
				new Map(current).set(cellKey(segment, persona.id), { criteriaId })
			);

			if (!isLinked(persona, segment)) {
				setLinked(segment, persona, true);
			}
		},
		[setLinked]
	);

	const clear = useCallback((segment: SegmentView, persona: PersonaView) => {
		setAssignments((current) => new Map(current).set(cellKey(segment, persona.id), null));
	}, []);

	const removeSegment = useCallback(
		(id: number) => {
			if (!draftOps.isTempId(id)) {
				setPendingSegmentDeletes((current) => [...current, id]);
			}

			edit((current) => draftOps.removeSegment(current, id));
		},
		[edit]
	);

	const removePersona = useCallback(
		(id: number) => {
			if (!draftOps.isTempId(id)) {
				setPendingPersonaDeletes((current) => [...current, id]);
			}

			edit((current) => draftOps.removePersona(current, id));
		},
		[edit]
	);

	const discard = useCallback(() => {
		setDraft(score ?? null);
		setAssignments(new Map());
		setPendingSegmentDeletes([]);
		setPendingPersonaDeletes([]);
		setError(null);
		setBlocker(null);
	}, [score]);

	const save = useCallback(
		async (remapCriteriaId?: (criteriaId: number) => number): Promise<boolean> => {
			if (!score || !draft) {
				return false;
			}

			const refusedRow = firstSaveBlocker(draft, score);

			setBlocker(refusedRow);

			if (refusedRow) {
				setError(texts.blocked(refusedRow));

				return false;
			}

			setSaving(true);
			setError(null);

			try {
				let savedScore: ScoreResponse | null = null;

				if (draftDirty) {
					savedScore = await updateScore(draftOps.toScoreBody(draft)).unwrap();
				}

				for (const id of pendingSegmentDeletes) {
					await deleteSegment(id).unwrap();
				}

				for (const id of pendingPersonaDeletes) {
					await deletePersona(id).unwrap();
				}

				const serverSegments = segmentsOf(score);
				const lookup = (segment: SegmentView | undefined, personaId: number) => {
					const found = segment ? findAssignment(criteria, segment, personaId) : null;

					return found
						? { id: found.criteriaId, name: found.criteriaName, ccpId: found.ccpId }
						: null;
				};

				// The save bar hands its click event over; only a real function remaps.
				const remap = typeof remapCriteriaId === "function" ? remapCriteriaId : undefined;
				const remapped = new Map(
					[...assignments].map(([key, pending]) => [
						key,
						pending && remap ? { criteriaId: remap(pending.criteriaId) } : pending,
					])
				);

				await saveSegmentAssignments(remapped, {
					assign: (payload) => assignCell(payload).unwrap(),
					remove: (ccpId) => removeCell(ccpId).unwrap(),
					getServerAssignment: (companyId, personaId) =>
						lookup(
							serverSegments.find((row) => row.id === companyId),
							personaId
						),
					getServerAssignmentForInvestor: (investorName, personaId) =>
						lookup(
							serverSegments.find((row) => row.investorName === investorName),
							personaId
						),
				});

				await refetch();
				// The draft becomes what the server now holds. The re-seed effect skips a dirty
				// draft, and a new segment keeps its temporary id in the draft, so without this
				// the page stayed "unsaved" and the next Save created the segment again (PREC-4381).
				setDraft(scoreSelector(store.getState()).score ?? null);
				setAssignments(new Map());
				setPendingSegmentDeletes([]);
				setPendingPersonaDeletes([]);

				if (savedScore) {
					onSaved?.(savedScore);
				}

				return true;
			} catch (error) {
				setError(saveErrorText(error, texts.saveError));

				return false;
			} finally {
				setSaving(false);
			}
		},
		[
			score,
			draft,
			draftDirty,
			criteria,
			assignments,
			pendingSegmentDeletes,
			pendingPersonaDeletes,
			updateScore,
			deleteSegment,
			deletePersona,
			assignCell,
			removeCell,
			refetch,
			texts,
			onSaved,
		]
	);

	return {
		draft,
		segments,
		personas,
		dirty,
		saving,
		error,
		blocker,
		isLinked: linked,
		assignmentOf,
		pick,
		clear,
		setLinked,
		rename: (kind, row, name) => edit((current) => draftOps.rename(current, kind, row, name)),
		updateSegment: (id, patch) => edit((current) => draftOps.updateSegment(current, id, patch)),
		// The id is drawn before the state update: React may run the updater later, and the
		// caller selects the new row by this id right away.
		addSegment: (group, seed) => {
			const id = draftOps.tempId();

			edit((current) => draftOps.addSegment(current, group, seed, id).score);

			return id;
		},
		removeSegment,
		setPartners: (on) => edit((current) => draftOps.setPartners(current, on)),
		setInvestors: (on) => edit((current) => draftOps.setInvestors(current, on)),
		setInvestorSelected: (name, selected) =>
			edit((current) => draftOps.setInvestorSelected(current, name, selected)),
		updatePersona: (id, patch) => edit((current) => draftOps.updatePersona(current, id, patch)),
		addPersona: (seed) => {
			const id = draftOps.tempId();

			edit((current) => draftOps.addPersona(current, seed, id).score);

			return id;
		},
		removePersona,
		save,
		discard,
	};
};
